import React, { useState, useMemo } from 'react';
import { Table, Project, TableColumn } from '../../types/domain';
import { PageContainer } from '../layout/PageContainer';
import { Copy, Check, Globe, Terminal, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { generateSnippetVariables } from '../../utils/schemaSnippets';
import { EndpointSnippetTabs } from './snippets';

interface EndpointsTabProps {
  table: Table;
  project: Project;
  columns?: TableColumn[];
}

export const EndpointsTab: React.FC<EndpointsTabProps> = ({ table, project, columns }) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8080';
  const resolvedToken = table.ingest_token && !table.ingest_token.includes('pk_live_default')
    ? table.ingest_token
    : (table.slug || table.id);
  const formUrl = `${origin}/api/v1/f/${resolvedToken}`;
  const webhookUrl = `${origin}/api/v1/f/${resolvedToken}`;
  const ingestToken = resolvedToken;
  const honeypot = table.honeypot_field || '_gotcha';
  const redirectUrl = table.redirect_url || `${origin}/thank-you`;

  const copyToClipboard = (key: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Generate single unified array of variables for all snippet components
  const variables = useMemo(
    () => generateSnippetVariables(columns, honeypot),
    [columns, honeypot]
  );

  return (
    <PageContainer variant="tab">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Form Action Endpoint */}
        <div className="p-3.5 bg-cf-surface border border-cf-border rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs uppercase text-cf-textSecondary flex items-center gap-1.5">
              <Globe size={13} className="text-cf-orange" />
              <span>HTML Form Endpoint</span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => copyToClipboard('form-url', formUrl)}
              title="Copy URL"
              aria-label="Copy URL"
            >
              {copiedKey === 'form-url' ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            </Button>
          </div>
          <div className="text-xs text-cf-textPrimary bg-cf-bg px-2.5 py-1.5 rounded border border-cf-border truncate select-text">
            {formUrl}
          </div>
        </div>

        {/* REST Webhook Endpoint */}
        <div className="p-3.5 bg-cf-surface border border-cf-border rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs uppercase text-cf-textSecondary flex items-center gap-1.5">
              <Terminal size={13} className="text-blue-400" />
              <span>REST Webhook Endpoint</span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => copyToClipboard('webhook-url', webhookUrl)}
              title="Copy URL"
              aria-label="Copy URL"
            >
              {copiedKey === 'webhook-url' ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            </Button>
          </div>
          <div className="text-xs text-cf-textPrimary bg-cf-bg px-2.5 py-1.5 rounded border border-cf-border truncate select-text">
            {webhookUrl}
          </div>
        </div>

        {/* Ingest Public Token */}
        <div className="p-3.5 bg-cf-surface border border-cf-border rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs uppercase text-cf-textSecondary flex items-center gap-1.5">
              <Shield size={13} className="text-emerald-400" />
              <span>Table Ingest Token</span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => copyToClipboard('token', ingestToken)}
              title="Copy Token"
              aria-label="Copy Token"
            >
              {copiedKey === 'token' ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            </Button>
          </div>
          <div className="text-xs text-cf-textPrimary bg-cf-bg px-2.5 py-1.5 rounded border border-cf-border truncate select-text">
            {ingestToken}
          </div>
        </div>
      </div>

      {/* 3. Tabbed Code Snippets: cURL | fetch | html with syntax highlighting */}
      <EndpointSnippetTabs
        variables={variables}
        formUrl={formUrl}
        webhookUrl={webhookUrl}
        ingestToken={ingestToken}
        redirectUrl={redirectUrl}
        honeypot={honeypot}
        isSchemaDriven={Boolean(columns && columns.length > 0)}
      />
    </PageContainer>
  );
};

